'use client'

import { z } from 'zod'

import { toast } from '@/core/lib/toast'

import { api } from '@/features/veluvit/services/api'
import { refreshAdult } from '@/features/veluvit/services/adult.service'
import {
  ensureAccount,
  parseBootstrap,
} from '@/features/veluvit/services/session.service'
import {
  mergeSettings,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'

import type { User } from '@/features/veluvit/types/api.types'

const settingsSchema = z.looseObject({
  showAdult: z.boolean().optional(),
})
type SettingsPatch = Pick<User['settings'], 'showAdult'>
type SettingsSave = {
  identity: number
  userId: string
  confirmed: User['settings']
  message: string | null
  running: boolean
}
let saving: SettingsSave | null = null

function currentAccount(save: SettingsSave) {
  const state = useSessionStore.getState()
  return (
    state.identity === save.identity && state.data?.user?.id === save.userId
  )
}

async function flushSettings(save: SettingsSave) {
  try {
    while (currentAccount(save)) {
      const state = useSessionStore.getState()
      const revision = state.settingsRevision
      const requested = state.data?.user?.settings
      if (!requested) return
      try {
        const data = parseBootstrap(
          await api<unknown>(
            '/profile/',
            { settings: { showAdult: requested.showAdult } },
            'PATCH'
          )
        )
        if (!currentAccount(save)) return
        if (data.user?.id !== save.userId)
          throw new Error('설정 저장 응답의 계정을 확인하지 못했어요.')
        const settings = settingsSchema.safeParse(data.user.settings ?? {})
        if (!settings.success)
          throw new Error(
            '설정 저장 응답을 확인하지 못했어요. 다시 시도해 주세요.'
          )
        save.confirmed = mergeSettings(requested, settings.data)
        let adultDenied = false
        const latest = useSessionStore.getState()
        const adultChanged =
          data.config.adult?.eligible === false &&
          latest.data?.config.adult?.eligible === true
        if (
          adultChanged ||
          (latest.settingsOverride?.showAdult && !data.config.adult?.eligible)
        ) {
          try {
            const adult = await refreshAdult()
            if (!currentAccount(save)) return
            adultDenied = !adult.eligible
          } catch (error) {
            const session = useSessionStore.getState().data
            if (
              currentAccount(save) &&
              session &&
              data.config.adult?.eligible === false
            ) {
              useSessionStore.setState({
                data: {
                  ...session,
                  config: { ...session.config, adult: data.config.adult },
                },
              })
            }
            throw error
          }
        }
        if (useSessionStore.getState().settingsRevision !== revision) continue
        useSessionStore.getState().syncSettings(save.confirmed, revision)
        if (adultDenied)
          toast.show(
            '언세이프 이용 권한을 확인하지 못했어요. 인증 상태를 확인해 주세요.'
          )
        else if (save.message !== null) toast.show(save.message)
        return
      } catch (error) {
        if (!currentAccount(save)) return
        if (useSessionStore.getState().settingsRevision !== revision) continue
        useSessionStore.getState().syncSettings(save.confirmed, revision)
        toast.show(
          error instanceof Error
            ? error.message
            : '설정을 저장하지 못했어요. 다시 시도해 주세요.'
        )
        return
      }
    }
  } finally {
    if (saving === save) saving = null
  }
}

function saveSettings(patch: SettingsPatch, message: string | null) {
  const state = useSessionStore.getState()
  if (!state.data?.user) {
    const identity = state.identity
    void ensureAccount()
      .then((user) => {
        const current = useSessionStore.getState()
        if (
          current.identity !== identity + 1 ||
          current.data?.user?.id !== user.id
        )
          return
        saveSettings(patch, message)
      })
      .catch((error: unknown) => {
        if (useSessionStore.getState().identity === identity)
          toast.show(
            error instanceof Error ? error.message : '계정을 확인하지 못했어요.'
          )
      })
    return
  }
  if (!saving || !currentAccount(saving))
    saving = {
      identity: state.identity,
      userId: state.data.user.id,
      confirmed: state.data.user.settings ?? {},
      message,
      running: false,
    }
  const save = saving
  save.message = message
  state.updateSettings(patch)
  if (!save.running) {
    save.running = true
    void flushSettings(save)
  }
}

export function saveShowAdult(on: boolean, message: string) {
  saveSettings({ showAdult: on }, message)
}
